import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { AuthenticatedUser } from '@ferremarket/shared'
import { api } from '../api/client'

interface AuthContextValue {
  user: AuthenticatedUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<AuthenticatedUser>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get<AuthenticatedUser>('/api/auth/me')
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  async function login(email: string, password: string): Promise<AuthenticatedUser> {
    const u = await api.post<AuthenticatedUser>('/api/auth/login', { email, password })
    const me = await api.get<AuthenticatedUser>('/api/auth/me')
    setUser(me)
    return me
  }

  async function logout() {
    await api.post('/api/auth/logout', {}).catch(() => null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside AuthProvider')
  return ctx
}
